import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Grid,
  Paper,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { campaignService } from '../services/campaignService';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

interface CampaignAnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  campaignId: number | null;
}

interface AnalyticsData {
  impressions: number;
  clicks: number;
  ctr: number;
  revenue_spent: number;
}

const CampaignAnalyticsModal: React.FC<CampaignAnalyticsModalProps> = ({ open, onClose, campaignId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [campaignTitle, setCampaignTitle] = useState<string>('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!token || !campaignId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const campaign = await campaignService.getCampaign(token, campaignId);
        setCampaignTitle(campaign.title);
        
        // In a real app, you would fetch detailed analytics here
        // For now, we'll use the campaign data we already have
        setAnalytics({
          impressions: campaign.impressions || 0,
          clicks: campaign.clicks || 0,
          ctr: campaign.clicks && campaign.impressions ? (campaign.clicks / campaign.impressions) * 100 : 0,
          revenue_spent: campaign.revenue_spent || 0
        });
      } catch (err) {
        console.error("Error fetching campaign analytics:", err);
        setError("Failed to load analytics data");
      } finally {
        setLoading(false);
      }
    };

    if (open && campaignId) {
      fetchAnalytics();
    }
  }, [open, campaignId, token]);

  const chartData = analytics ? [
    { name: 'Performance', impressions: analytics.impressions, clicks: analytics.clicks }
  ] : [];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1E293B',
          color: '#F1F5F9',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #334155', pb: 2 }}>
        Campaign Analytics: {campaignTitle}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="error">{error}</Typography>
          </Box>
        ) : analytics ? (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: '#0F172A', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                  <Typography variant="body1">Impressions: {analytics.impressions}</Typography>
                  <Typography variant="body1">Clicks: {analytics.clicks}</Typography>
                  <Typography variant="body1">CTR: {analytics.ctr.toFixed(2)}%</Typography>
                  <Typography variant="body1">Spent: ${analytics.revenue_spent.toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%', bgcolor: '#0F172A', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Visualization</Typography>
                  <Box sx={{ height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" stroke="#94A3B8" />
                        <YAxis stroke="#94A3B8" />
                        <RechartsTooltip contentStyle={{ backgroundColor: '#1E293B', borderColor: '#475569' }} />
                        <Legend />
                        <Bar dataKey="impressions" fill="#3B82F6" name="Impressions" />
                        <Bar dataKey="clicks" fill="#10B981" name="Clicks" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #334155', p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ 
            bgcolor: '#3B82F6', 
            '&:hover': { bgcolor: '#2563EB' },
            textTransform: 'none'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignAnalyticsModal; 